import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { ReactNode, useEffect, useId, useRef } from 'react';

// The chrome every segmented filter control shares: the label + chevron
// trigger, the scoped menu it opens, and the dismissal rules.
//
// Extracted from FilterSegment when the queues gained a Date range control
// (GRAVY-10315). A date range is two inputs rather than a checkbox list, but
// everything AROUND the body is identical — same trigger, same one-open-at-a-
// time contract, same outside-click and Escape handling, same focus return. The
// alternative was a second copy of that logic which would drift from this one.
//
// The body is whatever the caller renders; the shell owns only the shell.

type Props = {
  /** Placeholder when nothing is applied, e.g. "Client", "Date range". */
  label: string;
  /** What the trigger reads once something IS applied. */
  triggerText: string;
  /** Drives the trigger's active styling. */
  active: boolean;
  disabled?: boolean;
  /**
   * Open state is owned by the BAR, not the segment, so opening one control
   * closes the others. Left to each segment, clicking a sibling trigger leaves
   * both menus on screen — two overlapping panels over the queue.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Aligns the menu to the segment's right edge near the end of the bar. */
  align?: 'left' | 'right';
  /** ARIA role for the menu — a listbox of options, or a plain group. */
  menuRole?: 'listbox' | 'group';
  children: (menuId: string) => ReactNode;
};

const FilterSegmentShell = ({
  label,
  triggerText,
  active,
  disabled = false,
  open,
  onOpenChange,
  align = 'left',
  menuRole = 'listbox',
  children,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
        // Escape returns focus to the trigger, so keyboard users are not
        // dropped back at the top of the document.
        ref.current?.querySelector('button')?.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  return (
    <div className="dashv2-fseg" ref={ref}>
      <button
        type="button"
        className={`dashv2-fseg-btn${active ? ' is-active' : ''}`}
        aria-haspopup={menuRole === 'listbox' ? 'listbox' : 'dialog'}
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={disabled}
        onClick={() => onOpenChange(!open)}
      >
        <span className="dashv2-fseg-label">{triggerText}</span>
        {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </button>

      {open && (
        <div
          className={`dashv2-fseg-menu${align === 'right' ? ' align-right' : ''}`}
          id={menuId}
          role={menuRole}
          aria-multiselectable={menuRole === 'listbox' ? true : undefined}
          aria-label={label}
        >
          {children(menuId)}
        </div>
      )}
    </div>
  );
};

export default FilterSegmentShell;
